// NodeUrlField.jsx

import React, { useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Firebase
import { functions, httpsCallable } from 'firebaseConfig';

// Utilities
import { normalizeUrlForOpen } from 'utilities/Url';
import {
  URL_FETCH_STATUS,
  formatUrlFieldDisplay,
  getUrlString,
  normalizeUrlFieldValue,
} from 'utilities/UrlFieldValue';

// CSS
import './NodeUrlField.css';

// Icons
import Icon from 'svg/Icon';

// Components
import NodeUrlModal from './NodeUrlModal';
import TextInput from 'components/textinput/TextInput';

const refreshUrlField = httpsCallable(functions, 'nodeUrlRefresh');

function statusLabel(status) {
  switch (cleanStatus(status)) {
    case URL_FETCH_STATUS.PENDING:
    case URL_FETCH_STATUS.FETCHING:
      return 'Fetching…';
    case URL_FETCH_STATUS.READY:
      return 'Ready';
    case URL_FETCH_STATUS.FAILED:
      return 'Failed';
    default:
      return '';
  }
}

function cleanStatus(value) {
  return String(value || '').trim().toLowerCase();
}

/**
 * Displays a url field with unfurl metadata + modal editor.
 * In readOnly / previewMode: show value (+ open link); hide refresh, index checkbox, and edit modal.
 */
const NodeUrlField = ({
  node,
  field,
  fieldLabel,
  value,
  onChange,
  readOnly = false,
  previewMode = false,
}) => {
  const { theme } = useTheme();
  const isReadOnly = Boolean(readOnly || previewMode);
  const [urlOpen, setUrlOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const normalized = normalizeUrlFieldValue(value);
  const href = normalized?.url || getUrlString(value);
  const displayValue = formatUrlFieldDisplay(value) || '';
  const summary = normalized?.summary || '';
  const fetchStatus = normalized?.fetchStatus || URL_FETCH_STATUS.IDLE;
  const fetchError = normalized?.fetchError || '';
  const indexContent = normalized?.indexContent === true;
  const statusText = statusLabel(fetchStatus);
  const fieldId = String(field?.name || field?.fieldId || field?.key || '').trim();

  const handleNavigate = () => {
    const targetUrl = normalizeUrlForOpen(href);
    if (!targetUrl) return;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  const handleToggleIndex = (event) => {
    event.stopPropagation();
    if (isReadOnly || !href) return;
    const next = normalizeUrlFieldValue(value) || { url: href };
    onChange({
      ...next,
      url: href,
      indexContent: !indexContent,
    });
  };

  const handleRefresh = async (event) => {
    event.stopPropagation();
    if (isReadOnly || !href || !node?.key || !node?.baseKey || !fieldId || refreshing) return;
    setRefreshing(true);
    try {
      await refreshUrlField({
        baseKey: node.baseKey,
        nodeKey: node.key,
        fieldId,
      });
    } catch (error) {
      console.warn('[NodeUrlField] refresh failed', error);
    } finally {
      setRefreshing(false);
    }
  };

  const showMeta = Boolean(
    summary
    || (!isReadOnly && (statusText || fetchError || href)),
  );

  return (
    <>
      <div className={`node-url-field-container${isReadOnly ? ' is-readonly' : ''}`}>
        <div className="node-url-row">
          <TextInput
            style={{ cursor: isReadOnly ? 'default' : 'pointer' }}
            value={displayValue}
            readOnly
            placeholder={isReadOnly ? '' : 'Add a url...'}
            startIconKey="link"
            startIconColor={theme.foregroundMuted}
            iconSize={22}
            expandable
            multiline
            hybrid
            onClick={isReadOnly ? undefined : () => setUrlOpen(true)}
          />

          {href ? (
            <div
              className="node-url-navigate"
              title="Open URL"
              role="button"
              tabIndex={0}
              onClick={handleNavigate}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleNavigate();
                }
              }}
            >
              <Icon
                iconKey="open_in_new"
                color={theme.foregroundMuted}
                size={18}
              />
            </div>
          ) : null}
        </div>

        {showMeta ? (
          <div className="node-url-meta">
            {summary ? (
              <div className="node-url-summary" style={{ color: theme.foregroundMuted }}>
                {summary}
              </div>
            ) : null}

            {!isReadOnly ? (
              <div className="node-url-meta-row">

                {href ? (
                  <label className="node-url-index-inline" style={{ color: theme.foregroundMuted }}>
                    <input
                      type="checkbox"
                      checked={indexContent}
                      onChange={handleToggleIndex}
                    />
                    Index this page
                  </label>
                ) : null}
              </div>
            ) : null}

            {!isReadOnly && fetchError ? (
              <div className="node-url-error" style={{ color: theme.danger || theme.foregroundMuted }}>
                {fetchError}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {!isReadOnly ? (
        <NodeUrlModal
          label={fieldLabel}
          value={value}
          isOpen={urlOpen}
          setOpen={setUrlOpen}
          onChange={onChange}
        />
      ) : null}
    </>
  );
};

NodeUrlField.displayName = 'NodeUrlField';

export default NodeUrlField;
